// Admin Dashboard — Steve's god-mode landing page.
//
// Spec: TKOS_MASTER_CONTEXT.md §ADMIN PORTAL + inventory_sections/02_admin_supplier.md.
// Source-of-truth render lives in admin-standalone.html ~1009-1257 (page-home),
// with the Platform Commission widget bolted on at lines 3826-3992.
//
// Data sources
// ------------
// Live endpoints (verified 2026-05 against /var/www/kitchenos-api/server.js):
//   GET /api/stripe/admin/overview  — REAL. MTD/YTD/all-time commission, byRetailer,
//                                      tierDistribution. modules/stripe.js:446.
//   GET /api/admin/retailers        — REAL. Used for retailer counts + recent signups.
//                                      server.js:892.
//   GET /api/admin/stats            — REAL but social-heavy. We pluck `retailers` only.
//                                      server.js:864.
//
// Stubbed (no backend yet — we synthesise from `/api/admin/retailers`):
//   - Recent transactions feed       — derive nothing real; render demo placeholder.
//   - New-signups sparkline (7d)     — synthesised from createdAt timestamps.
//   - "Active retailers < 30d login" — last-login isn't in /api/admin/retailers,
//                                       so we fall back to `status==='active'` count.
//
// AI conversation monitor
// -----------------------
// Master doc says this should exist, inventory_sections/02_admin_supplier.md flags
// it as completely absent. We render the spec'd panel structure with a clear
// "coming soon" empty state so the slot is reserved.

import { ApiError } from "@tkos/api-client";
import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../types";

import "./styles.css";

// ── Server response shapes ───────────────────────────────────────────────────

interface ServerRetailer {
  id: string;
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  status: string;
  createdAt: string;
  /** Some records carry stripeConnected on `settings`; absent otherwise. */
  settings?: {
    stripeConnected?: boolean;
    lastLoginAt?: string;
  };
}

interface ByRetailerRow {
  retailerId: string;
  commissionPence: number;
  gmvPence: number;
  txCount: number;
}

interface AdminOverview {
  totalMtdCommissionPence: number;
  totalYtdCommissionPence: number;
  totalAllTimeCommissionPence: number;
  txCount: number;
  byRetailer: ByRetailerRow[];
  tierDistribution: Record<string, number>;
}

interface RecentTx {
  id: string;
  retailerId: string;
  retailerName: string;
  grossPence: number;
  customer: string;
  createdAt: number;
  status: string;
}

// ── View model ───────────────────────────────────────────────────────────────

interface SignupRow {
  id: string;
  businessName: string;
  email: string;
  createdAt: string;
  onboardingComplete: boolean;
}

interface DashboardView {
  totals: {
    retailers: number;
    retailersDeltaPct: number; // vs last month
    activeRetailers: number;
    turnoverMtdGbp: number;
    commissionMtdGbp: number;
    newSignupsThisWeek: number;
    signupSparkline: number[]; // last 7 days
    stripeConnectedPct: number;
    stripeConnectedCount: number;
  };
  recentSignups: SignupRow[];
  recentTx: RecentTx[];
  /** True when any block fell back to synthesised values. */
  partial: boolean;
}

// ── Formatters ───────────────────────────────────────────────────────────────

const GBP_BIG = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const GBP_2DP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const RTF = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });

function gbpFromPence2dp(pence: number): string {
  return GBP_2DP.format(pence / 100);
}

function relTime(at: number | string): string {
  const t = typeof at === "number" ? at : Date.parse(at);
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.round((t - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return RTF.format(diffSec, "second");
  if (abs < 3600) return RTF.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return RTF.format(Math.round(diffSec / 3600), "hour");
  if (abs < 86400 * 30) return RTF.format(Math.round(diffSec / 86400), "day");
  if (abs < 86400 * 365) return RTF.format(Math.round(diffSec / (86400 * 30)), "month");
  return RTF.format(Math.round(diffSec / (86400 * 365)), "year");
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// ── Demo / fallback bits ─────────────────────────────────────────────────────

/** Three placeholder transactions used when no backend feed exists. */
function demoTransactions(): RecentTx[] {
  const now = Date.now();
  return [
    { id: "demo-1", retailerId: "turner",  retailerName: "Turner Kitchens",      grossPence: 1248_00, customer: "S. Mitchell", createdAt: now - 18 * 60_000,        status: "succeeded" },
    { id: "demo-2", retailerId: "northern", retailerName: "Northern Kitchens",   grossPence:  892_00, customer: "P. Patel",    createdAt: now -  2 * 3_600_000,    status: "succeeded" },
    { id: "demo-3", retailerId: "harrog",  retailerName: "Harrogate Interiors", grossPence: 2480_00, customer: "J. Hargreaves", createdAt: now -  6 * 3_600_000,   status: "succeeded" },
  ];
}

// ── Backend fetch ────────────────────────────────────────────────────────────

async function loadView(
  ctx: PageContext,
  signal: AbortSignal,
): Promise<DashboardView> {
  const [retailersRes, overviewRes, txRes] = await Promise.allSettled([
    ctx.api<ServerRetailer[]>("/admin/retailers", { signal }),
    ctx.api<AdminOverview>("/stripe/admin/overview", { signal }),
    ctx.api<{ items: RecentTx[] }>("/admin/transactions/recent?limit=10", { signal }),
  ]);

  if (signal.aborted) throw new DOMException("aborted", "AbortError");

  const retailers: ServerRetailer[] =
    retailersRes.status === "fulfilled" ? retailersRes.value : [];
  const overview: AdminOverview | null =
    overviewRes.status === "fulfilled" ? overviewRes.value : null;

  // Recent transactions — backend doesn't expose this yet; we render a demo
  // placeholder rather than empty.
  let recentTx: RecentTx[];
  let txStubbed = false;
  if (txRes.status === "fulfilled" && Array.isArray(txRes.value?.items)) {
    recentTx = txRes.value.items.slice(0, 10);
  } else {
    recentTx = demoTransactions();
    txStubbed = true;
  }

  // ── Totals derived from /admin/retailers + /stripe/admin/overview ─────────

  const totalRetailers = retailers.length;

  // "Active < 30 days login" — last-login isn't on the retailer payload yet,
  // so we approximate with status==='active'. Flag this as partial.
  const activeRetailers = retailers.filter((r) => r.status === "active").length;

  // Stripe-connected — `settings.stripeConnected` is optional. Default to 0%.
  const stripeConnectedCount = retailers.filter(
    (r) => r.settings?.stripeConnected === true,
  ).length;
  const stripeConnectedPct =
    totalRetailers > 0
      ? Math.round((stripeConnectedCount / totalRetailers) * 100)
      : 0;

  // Delta vs last month — synthesise from createdAt timestamps. Count signups
  // this month vs last month and express as percent change.
  const now = new Date();
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
  const thisMonthCount = retailers.filter(
    (r) => Date.parse(r.createdAt) >= startOfThisMonth,
  ).length;
  const lastMonthCount = retailers.filter((r) => {
    const t = Date.parse(r.createdAt);
    return t >= startOfLastMonth && t < startOfThisMonth;
  }).length;
  const retailersDeltaPct =
    lastMonthCount > 0
      ? Math.round(((thisMonthCount - lastMonthCount) / lastMonthCount) * 100)
      : thisMonthCount > 0
      ? 100
      : 0;

  // Turnover MTD — sum of gross pence from byRetailer. byRetailer is all-time,
  // so we approximate "MTD turnover" by scaling from MTD commission / all-time
  // commission against all-time GMV. If we can't, fall back to all-time GMV.
  const allTimeGmvPence = (overview?.byRetailer ?? []).reduce(
    (s, r) => s + r.gmvPence,
    0,
  );
  const allTimeCommission = overview?.totalAllTimeCommissionPence ?? 0;
  const mtdCommission = overview?.totalMtdCommissionPence ?? 0;
  const turnoverMtdPence =
    allTimeCommission > 0
      ? Math.round((mtdCommission / allTimeCommission) * allTimeGmvPence)
      : 0;
  const turnoverMtdGbp = turnoverMtdPence / 100;
  const commissionMtdGbp = mtdCommission / 100;

  // Signups this week + 7-day sparkline.
  const dayMs = 24 * 60 * 60 * 1000;
  const startOfWeek = Date.now() - 7 * dayMs;
  const newSignupsThisWeek = retailers.filter(
    (r) => Date.parse(r.createdAt) >= startOfWeek,
  ).length;
  const signupSparkline: number[] = [];
  for (let i = 6; i >= 0; i--) {
    const dayStart = Date.now() - (i + 1) * dayMs;
    const dayEnd = dayStart + dayMs;
    const count = retailers.filter((r) => {
      const t = Date.parse(r.createdAt);
      return t >= dayStart && t < dayEnd;
    }).length;
    signupSparkline.push(count);
  }

  // Recent signups — sort by createdAt desc, take 10.
  const recentSignups: SignupRow[] = retailers
    .filter((r) => r.createdAt)
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))
    .slice(0, 10)
    .map((r) => ({
      id: r.id,
      businessName: r.businessName || "(unnamed)",
      email: r.email || "",
      createdAt: r.createdAt,
      onboardingComplete: r.status === "active",
    }));

  // Hydrate retailerName on recentTx using the retailers list when missing.
  const nameById = new Map(retailers.map((r) => [r.id, r.businessName]));
  for (const t of recentTx) {
    if (!t.retailerName) t.retailerName = nameById.get(t.retailerId) ?? t.retailerId;
  }

  const partial = txStubbed || retailers.length === 0 || overview === null;

  return {
    totals: {
      retailers: totalRetailers,
      retailersDeltaPct,
      activeRetailers,
      turnoverMtdGbp,
      commissionMtdGbp,
      newSignupsThisWeek,
      signupSparkline,
      stripeConnectedPct,
      stripeConnectedCount,
    },
    recentSignups,
    recentTx,
    partial,
  };
}

// ── Stat-card spec ───────────────────────────────────────────────────────────

interface StatCardSpec {
  key: string;
  label: string;
  icon: string;
  accent: string; // CSS var
  value: string;
  sub: string;
  subTone: "muted" | "ok" | "alert";
  /** Optional sparkline data (0-6 indexed). */
  sparkline?: number[];
}

function buildCards(view: DashboardView): StatCardSpec[] {
  const t = view.totals;
  const deltaSign = t.retailersDeltaPct > 0 ? "+" : t.retailersDeltaPct < 0 ? "" : "";
  return [
    {
      key: "totalRetailers",
      label: "Total retailers",
      icon: "🏪",
      accent: "var(--adm-info)",
      value: String(t.retailers),
      sub:
        t.retailers === 0
          ? "No retailers yet"
          : `${deltaSign}${t.retailersDeltaPct}% vs last month`,
      subTone: t.retailersDeltaPct >= 0 ? "ok" : "alert",
    },
    {
      key: "activeRetailers",
      label: "Active retailers",
      icon: "✅",
      accent: "var(--adm-ok)",
      value: String(t.activeRetailers),
      sub: "Status = active",
      subTone: "muted",
    },
    {
      key: "turnover",
      label: "Platform turnover MTD",
      icon: "💷",
      accent: "var(--adm-primary)",
      value: GBP_BIG.format(t.turnoverMtdGbp),
      sub: "All retailers · this month",
      subTone: "muted",
    },
    {
      key: "commission",
      label: "Commission MTD",
      icon: "💰",
      accent: "var(--adm-primary)",
      value: GBP_BIG.format(t.commissionMtdGbp),
      sub: "Our cut this month",
      subTone: "ok",
    },
    {
      key: "signups",
      label: "New signups (7d)",
      icon: "📈",
      accent: "#a855f7",
      value: String(t.newSignupsThisWeek),
      sub: "Last 7 days",
      subTone: "muted",
      sparkline: t.signupSparkline,
    },
    {
      key: "stripe",
      label: "Stripe-connected",
      icon: "🔗",
      accent: "#22c55e",
      value: `${t.stripeConnectedPct}%`,
      sub: `${t.stripeConnectedCount} of ${t.retailers} retailers`,
      subTone: t.stripeConnectedPct >= 50 ? "ok" : "alert",
    },
  ];
}

// ── Rendering ────────────────────────────────────────────────────────────────

function renderSparkline(values: number[]): string {
  if (values.length === 0) return "";
  const max = Math.max(...values, 1);
  const w = 64;
  const h = 18;
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  const pts = values
    .map((v, i) => {
      const x = i * step;
      const y = h - (v / max) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return `
    <svg class="dash-spark" viewBox="0 0 ${w} ${h}" aria-hidden="true">
      <polyline points="${pts}" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" />
    </svg>
  `;
}

function renderCards(host: HTMLElement, view: DashboardView): void {
  const cards = buildCards(view);
  host.innerHTML = cards
    .map((c) => {
      const subClass =
        c.subTone === "alert"
          ? "dash-card__sub dash-card__sub--alert"
          : c.subTone === "ok"
          ? "dash-card__sub dash-card__sub--ok"
          : "dash-card__sub";
      const spark = c.sparkline ? renderSparkline(c.sparkline) : "";
      return `
        <article class="dash-card" data-key="${escapeHtml(c.key)}"
          style="--dash-card-accent:${c.accent}">
          <div class="dash-card__top">
            <span class="dash-card__icon" aria-hidden="true">${c.icon}</span>
            <span class="dash-card__label">${escapeHtml(c.label)}</span>
          </div>
          <div class="dash-card__value">${escapeHtml(c.value)}</div>
          <div class="dash-card__foot">
            <span class="${subClass}">${escapeHtml(c.sub)}</span>
            ${spark}
          </div>
        </article>
      `;
    })
    .join("");
}

function renderSignups(host: HTMLElement, rows: SignupRow[]): void {
  if (rows.length === 0) {
    host.innerHTML = `<div class="adm-empty">No recent signups.</div>`;
    return;
  }
  host.innerHTML = `
    <table class="adm-table dash-table">
      <thead>
        <tr>
          <th>Business</th>
          <th>Email</th>
          <th>Joined</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        ${rows
          .map(
            (r) => `
            <tr>
              <td><b>${escapeHtml(r.businessName)}</b></td>
              <td class="dash-cell-muted">${escapeHtml(r.email || "—")}</td>
              <td class="dash-cell-muted">${escapeHtml(relTime(r.createdAt))}</td>
              <td>${
                r.onboardingComplete
                  ? `<span class="adm-pill adm-pill-ok">Onboarded</span>`
                  : `<span class="adm-pill adm-pill-warn">In progress</span>`
              }</td>
            </tr>
          `,
          )
          .join("")}
      </tbody>
    </table>
  `;
}

function renderTransactions(host: HTMLElement, rows: RecentTx[], demo: boolean): void {
  if (rows.length === 0) {
    host.innerHTML = `<div class="adm-empty">No transactions yet.</div>`;
    return;
  }
  const badge = demo
    ? `<span class="adm-pill adm-pill-warn dash-section__badge">Demo data</span>`
    : "";
  host.innerHTML = `
    <div class="dash-section__head">
      <h3 class="dash-section__title">Recent transactions</h3>
      ${badge}
    </div>
    <table class="adm-table dash-table">
      <thead>
        <tr>
          <th>Retailer</th>
          <th>Customer</th>
          <th class="dash-num">Amount</th>
          <th>When</th>
        </tr>
      </thead>
      <tbody>
        ${rows
          .map(
            (t) => `
            <tr>
              <td><b>${escapeHtml(t.retailerName)}</b></td>
              <td class="dash-cell-muted">${escapeHtml(t.customer || "—")}</td>
              <td class="dash-num"><b>${escapeHtml(gbpFromPence2dp(t.grossPence))}</b></td>
              <td class="dash-cell-muted">${escapeHtml(relTime(t.createdAt))}</td>
            </tr>
          `,
          )
          .join("")}
      </tbody>
    </table>
  `;
}

function renderAiMonitor(host: HTMLElement): void {
  // Inventory flags: not built in legacy. Master doc says it should exist.
  // We render the spec'd table shell with an explicit "coming soon" empty state
  // so the slot is reserved and the IA matches the master doc.
  host.innerHTML = `
    <div class="dash-section__head">
      <h3 class="dash-section__title">AI conversation monitor</h3>
      <span class="adm-pill adm-pill-info dash-section__badge">Coming soon</span>
    </div>
    <table class="adm-table dash-table">
      <thead>
        <tr>
          <th>Portal</th>
          <th>Retailer</th>
          <th>Assistant</th>
          <th>Last message</th>
          <th>Flagged</th>
        </tr>
      </thead>
      <tbody>
        <tr><td colspan="5" class="adm-empty" style="padding:1.5rem">
          AI conversation feeds (PA AI, Customer AI, Supplier AI, Kit-Q) will surface here for live moderation. Backend endpoints pending.
        </td></tr>
      </tbody>
    </table>
  `;
}

function renderHealthPill(
  host: HTMLElement,
  ctx: PageContext,
): { dispose: () => void } {
  host.innerHTML = `
    <button class="dash-health" type="button" data-role="health-link">
      <span class="dash-health__dot" aria-hidden="true"></span>
      <span class="dash-health__text">
        <span class="dash-health__title">System health</span>
        <span class="dash-health__sub" data-role="health-sub">Live · open System Health page</span>
      </span>
      <span class="dash-health__chev" aria-hidden="true">›</span>
    </button>
  `;
  const btn = host.querySelector<HTMLButtonElement>("[data-role='health-link']");
  const handler = (): void => {
    ctx.navigate("system-health");
  };
  btn?.addEventListener("click", handler);
  return {
    dispose: (): void => {
      btn?.removeEventListener("click", handler);
    },
  };
}

function renderShell(container: HTMLElement): {
  bannerEl: HTMLElement;
  cardsEl: HTMLElement;
  signupsEl: HTMLElement;
  txEl: HTMLElement;
  aiEl: HTMLElement;
  healthEl: HTMLElement;
  refreshBtn: HTMLButtonElement;
  metaEl: HTMLElement;
} {
  container.innerHTML = `
    <section class="dash-root" aria-label="Platform dashboard">
      <header class="dash-header">
        <div>
          <h1 class="dash-title">Platform Dashboard</h1>
          <p class="dash-subtitle">Real-time platform turnover, commission and signups across all retailers.</p>
        </div>
        <div class="dash-header__meta">
          <span data-role="meta">Loading…</span>
          <button class="adm-btn adm-btn-ghost" type="button" data-role="refresh">Refresh</button>
        </div>
      </header>

      <div class="dash-banner" data-role="banner" hidden></div>

      <div class="dash-cards" data-role="cards"></div>

      <div class="dash-grid">
        <section class="adm-card dash-section" data-role="signups-section">
          <div class="dash-section__head">
            <h3 class="dash-section__title">Recent signups</h3>
          </div>
          <div data-role="signups"></div>
        </section>
        <section class="adm-card dash-section" data-role="tx-section">
          <div data-role="tx"></div>
        </section>
      </div>

      <section class="adm-card dash-section" data-role="ai-section">
        <div data-role="ai"></div>
      </section>

      <div data-role="health"></div>
    </section>
  `;

  return {
    bannerEl: container.querySelector<HTMLElement>("[data-role='banner']")!,
    cardsEl: container.querySelector<HTMLElement>("[data-role='cards']")!,
    signupsEl: container.querySelector<HTMLElement>("[data-role='signups']")!,
    txEl: container.querySelector<HTMLElement>("[data-role='tx']")!,
    aiEl: container.querySelector<HTMLElement>("[data-role='ai']")!,
    healthEl: container.querySelector<HTMLElement>("[data-role='health']")!,
    refreshBtn: container.querySelector<HTMLButtonElement>("[data-role='refresh']")!,
    metaEl: container.querySelector<HTMLElement>("[data-role='meta']")!,
  };
}

// ── Page ─────────────────────────────────────────────────────────────────────

const REFRESH_MS = 60_000;

const dashboardPage: Page = {
  name: "dashboard",
  label: "Dashboard",
  icon: "🏠",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const { bannerEl, cardsEl, signupsEl, txEl, aiEl, healthEl, refreshBtn, metaEl } =
      renderShell(container);

    let controller: AbortController | null = null;
    let timer: number | null = null;
    let disposed = false;

    // Static panels — render once.
    renderAiMonitor(aiEl);
    const healthHandle = renderHealthPill(healthEl, ctx);

    const setBanner = (msg: string | null, kind: "info" | "error" = "info"): void => {
      if (!msg) {
        bannerEl.hidden = true;
        bannerEl.textContent = "";
        bannerEl.classList.remove("dash-banner--error");
        return;
      }
      bannerEl.hidden = false;
      bannerEl.textContent = msg;
      bannerEl.classList.toggle("dash-banner--error", kind === "error");
    };

    const refresh = async (announce: boolean): Promise<void> => {
      if (disposed) return;

      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;

      refreshBtn.disabled = true;

      try {
        const view = await loadView(ctx, signal);
        if (disposed || signal.aborted) return;

        renderCards(cardsEl, view);
        renderSignups(signupsEl, view.recentSignups);
        const txStubbed = view.recentTx.length > 0 && view.recentTx[0]?.id?.startsWith("demo-") === true;
        renderTransactions(txEl, view.recentTx, txStubbed);

        const now = new Date();
        metaEl.textContent = `Updated ${now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;

        if (view.partial) {
          setBanner(
            "Some backend endpoints aren't live yet (recent transactions, last-login). Showing best-effort figures from the data we have.",
            "info",
          );
        } else {
          setBanner(null);
        }

        if (announce) ctx.showToast("Dashboard refreshed", "success");
      } catch (err) {
        if (signal.aborted) return;
        const msg =
          err instanceof ApiError
            ? `Couldn't load dashboard (HTTP ${err.status})`
            : err instanceof Error
            ? err.message
            : "Couldn't load dashboard";
        setBanner(msg, "error");
        if (announce) ctx.showToast(msg, "error");
      } finally {
        if (!disposed) refreshBtn.disabled = false;
      }
    };

    refreshBtn.addEventListener("click", () => {
      void refresh(true);
    });

    void refresh(false);

    timer = window.setInterval(() => {
      void refresh(false);
    }, REFRESH_MS);

    return (): void => {
      disposed = true;
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
      controller?.abort();
      controller = null;
      healthHandle.dispose();
    };
  },
};

registerPage(dashboardPage);

export default dashboardPage;
